import { Tooltip as TooltipPrimitive } from 'radix-ui';
import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/cn';

/**
 * Tooltip — the supplementary label (`FE-04.10`).
 *
 * ── Two things it must do that Radix does not do by default ───────────────
 *
 * 1. **It opens on focus as well as hover.** `FR-COLLAB-006` AC2 is written as a
 *    hover behaviour, and a hover-only tooltip is a mouse-only action — against
 *    cross-cutting principle 6. Two of the three Stage 2 returns insist on this
 *    independently. Radix's trigger opens on `focus` with no delay, so this
 *    costs nothing but must not be undone by a caller.
 *
 * 2. **It works on a `disabled` trigger.** Radix's tooltip never fires on a
 *    disabled button: a disabled form control dispatches no pointer events and
 *    takes no focus. `entry-surfaces.md` asks B3 to ship the wrapper-span
 *    workaround here, *"because `disabledReason` is the only thing making a
 *    locked control honest"* — a capability the viewer does not hold is locked
 *    with a stated reason, not hidden (`FR-PERM-012` AC1), and a reason only a
 *    mouse can reach is not a reason. Shipped **once, inside the primitive**, so
 *    no caller has to know.
 *
 *    The wrapper is added only for a real `disabled`, because an always-on
 *    wrapper would put a span into every flex row in the product. It carries
 *    `tabIndex={0}`: that is a deliberate deviation from native disabled
 *    behaviour — the control stays out of reach, its *reason* does not.
 *
 *    ⚠ **`aria-disabled` deliberately does not trigger it.** An `aria-disabled`
 *    element is still focusable and still receives pointer events — that is the
 *    entire reason the idiom exists — so wrapping one would add a second tab
 *    stop and `pointer-events-none` would kill a handler the caller kept. Note
 *    that this inspects the *authored props*, not the rendered DOM: a
 *    `<Button disabled disabledReason />` is caught here whether it renders a
 *    `<button disabled>` or an `<a aria-disabled>`.
 *
 * ── What it is not ────────────────────────────────────────────────────────
 * **Never the accessible name.** Radix wires the content to the trigger through
 * `aria-describedby`, so a tooltip is a *description*; an `IconButton` still
 * carries its own required `label`. And `shortcut` is displayed, never bound —
 * keyboard shortcuts are `B41`'s (B3-BRIEF §8). Printing the key here is how
 * `f`, `x`, `q` and `/` become discoverable before a shortcuts sheet exists
 * (`finding-things.md` Contradiction 8).
 */

/**
 * Whether a `TooltipProvider` sits above this tooltip. Our own flag, because
 * Radix offers no public way to ask whether its provider context is present.
 */
const SharedTooltipProviderContext = createContext(false);

export interface TooltipProviderProps {
  /** The hover delay for tooltips that do not pass their own. Default 300ms. */
  delay?: number;
  children: ReactNode;
}

/**
 * One Radix provider for a whole region — member initials
 * (`docs/notes/member-initials.md`) mounts it around the board face, where
 * every card front's avatars carry a tooltip.
 *
 * Two things it buys over the per-instance provider `Tooltip` falls back to:
 * one provider for a board's worth of avatar tooltips instead of one each, and
 * `skipDelayDuration` working at all — moving from one avatar to the next one
 * opens its tooltip at once instead of re-waiting the delay, which is what a
 * row of adjacent circles asks for.
 *
 * Optional by design: a `Tooltip` outside any `TooltipProvider` still mounts
 * its own, exactly as before, so no existing call site or test changes.
 */
export function TooltipProvider({ delay = 300, children }: TooltipProviderProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={delay}>
      <SharedTooltipProviderContext.Provider value={true}>
        {children}
      </SharedTooltipProviderContext.Provider>
    </TooltipPrimitive.Provider>
  );
}

export interface TooltipProps {
  /** The description. Kept short — a tooltip is never the only carrier of information. */
  content: ReactNode;
  /**
   * A keyboard shortcut to print alongside the content. **Displayed only.**
   * This primitive binds nothing.
   */
  shortcut?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Hover delay in milliseconds. Focus always opens immediately. */
  delay?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  /** A single element that accepts a ref — a `Button`, an `IconButton`, an `<a>`. */
  children: ReactElement;
}

/** Does this element render as a control the browser will refuse to focus? */
function looksDisabled(child: ReactNode): boolean {
  if (!isValidElement(child)) return false;
  const props = child.props as { disabled?: unknown };
  return props.disabled === true;
}

export function Tooltip({
  content,
  shortcut,
  side = 'top',
  delay = 300,
  open,
  onOpenChange,
  className,
  children,
}: TooltipProps) {
  const disabled = looksDisabled(children);
  const shared = useContext(SharedTooltipProviderContext);

  // Spread conditionally rather than passing `undefined`: Radix declares
  // `open?: boolean` without `| undefined`, and `exactOptionalPropertyTypes` is
  // on — passing the key at all is what puts Radix into controlled mode.
  const rootProps = {
    ...(open === undefined ? {} : { open }),
    ...(onOpenChange === undefined ? {} : { onOpenChange }),
  };

  const trigger = disabled ? (
    /*
     * The workaround. A disabled control swallows pointer events and cannot be
     * focused, so the events retarget to this wrapper — which is focusable so
     * the keyboard path survives too. `pointer-events-none` on the child keeps
     * the retargeting reliable across browsers.
     */
    <span tabIndex={0} className="inline-flex">
      {cloneElement(children as ReactElement<{ className?: string }>, {
        className: cn(
          (children.props as { className?: string }).className,
          'pointer-events-none',
        ),
      })}
    </span>
  ) : (
    children
  );

  const tooltip =
    (
      /*
       * `delayDuration` on the Root as well as the fallback Provider below, so a
       * caller's `delay` still holds under a shared `TooltipProvider` — Radix's
       * Root value overrides its provider's.
       */
      <TooltipPrimitive.Root delayDuration={delay} {...rootProps}>
        <TooltipPrimitive.Trigger asChild>{trigger}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              // Opaque and bordered: the same reason the Dialog is. A tooltip
              // floats over a board whose colour is data, so it cannot borrow
              // contrast from what is behind it.
              'z-50 flex max-w-[18rem] items-center gap-2 rounded-card border border-border-strong bg-surface-raised px-2 py-1 text-xs text-ink shadow-card',
              className,
            )}
          >
            <span>{content}</span>
            {shortcut ? (
              <kbd className="rounded-sm border border-border px-1 font-sans text-ink-muted">
                {shortcut}
              </kbd>
            ) : null}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    );

  if (shared) return tooltip;

  return (
    /*
     * Outside a `TooltipProvider` the Provider is per instance: Radix throws
     * without one, and `main.tsx` belongs to the parent session. The cost is
     * `skipDelayDuration` — moving between two adjacent tooltips re-waits the
     * delay. A region that renders many adjacent tooltips (the board face's
     * card-front avatars) mounts one `TooltipProvider` instead; an
     * application-level one is still a shell (`FE-04.11`) decision.
     */
    <TooltipPrimitive.Provider delayDuration={delay}>{tooltip}</TooltipPrimitive.Provider>
  );
}
